import React from 'react';
import { Box, Container, Typography, Divider, Link } from '@mui/material';
import { TopNavBar } from './shared/TopNavBar';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: '#1e293b' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P: React.FC<{ children: React.ReactNode; bold?: boolean }> = ({ children, bold }) => (
  <Typography
    variant="body2"
    sx={{
      mb: 1.2,
      lineHeight: 1.9,
      color: '#374151',
      fontWeight: bold ? 700 : 400,
    }}
  >
    {children}
  </Typography>
);

const Item: React.FC<{ children: React.ReactNode; bold?: boolean }> = ({ children, bold }) => (
  <Typography
    variant="body2"
    component="li"
    sx={{
      mb: 0.8,
      lineHeight: 1.9,
      color: '#374151',
      fontWeight: bold ? 700 : 400,
      ml: 2,
    }}
  >
    {children}
  </Typography>
);

const TermsPage: React.FC = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <TopNavBar />
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* 标题区 */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#0f172a', mb: 1 }}>
            Xup 用户服务协议
          </Typography>
          <Typography variant="body2" color="text.secondary">
            更新日期：2026年 4月 18日 &nbsp;｜&nbsp; 生效日期：2026年 4月 18日
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <P>
          亲爱的用户，欢迎您使用 Xup 产品及服务！
        </P>
        <P>
          在使用本服务前，请您务必仔细阅读并理解本《Xup 用户服务协议》（以下简称"本协议"）以及本平台的其他相关协议、政策或指引。您在使用本服务某一特定功能时，针对该特定功能可能会另有单独的协议、相关业务规则等（以下简称"具体协议"）。本协议与具体协议的内容存在冲突的，以具体协议的规定为准。所有前述条款和规则构成本协议不可分割的组成部分，与本协议正文具有同等法律效力。
        </P>
        <P bold>
          我们特别提醒您在使用本服务之前认真阅读（未满18周岁的未成年人应在法定监护人陪同下阅读）、充分理解本协议的全部条款，尤其是加粗显示的条款。当您通过网络页面点击确认、勾选等方式同意本协议或实际使用本服务时，均表示您已接受所述的所有条款及其适用条件，同意受本协议约束。如果您不同意本协议的任一内容，请立即停止使用本服务。
        </P>

        <Section title="1. 服务内容">
          <P>
            1.1 Xup（以下简称"本平台"或"本服务"）是一个以 AI 技术为核心的创作平台，主要为用户提供 AI 视频生成、AI 图片生成等创作工具及相关服务。本平台通过整合多家主流 AI 服务提供商（包括但不限于 Google Veo、OpenAI Sora、xAI Grok 等）的能力，为用户提供统一、便捷的创作入口。
          </P>
          <P>
            1.2 本平台不直接提供底层 AI 模型服务，而是通过合法的 API 接入方式，将第三方 AI 能力集成到平台中。用户通过本平台使用上述服务时，同时受相应第三方服务提供商用户协议的约束，请在使用前自行了解。
          </P>
          <P>
            1.3 随着技术和法律法规的发展变化，我们可能对服务进行新增、升级、变更、中止或终止，并对相关技术、方式、性能等进行必要调整，同时可能针对新功能开展内部或外部测试。
          </P>
          <P>
            1.4 我们将采取不低于行业实践的必要措施，保障平台的网络安全和稳定运行，并持续提升用户体验。如您对我们的服务有任何问题或反馈，可通过第 9 条所列方式与我们联系。
          </P>
        </Section>

        <Section title="2. 账号管理">
          <P>
            2.1 本服务主要面向成年人。如果您未满 18 周岁，请在法定监护人陪同下阅读本协议，并在征得法定监护人同意后使用本服务。
          </P>
          <P>
            2.2 您在使用本服务前，应通过页面指引注册账号，按要求填写真实、准确、合法、有效的相关信息，并确认同意本协议及其他相关规则。若您提供的信息不准确、不真实或不合法，我们有权拒绝为您提供相关功能，或限制您使用部分服务。您的账号不可转让、不可赠与、不可继承。
          </P>
          <P>2.3 在您注册和使用账号时，您须承诺和保证：</P>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            <Item>对注册信息的真实性、合法性、有效性承担全部责任，不得冒用他人名义注册或使用本服务；</Item>
            <Item>妥善保管账号及密码，并对该账号项下的所有行为承担法律责任；</Item>
            <Item>不得恶意注册账号，包括但不限于频繁注册、批量注册等；</Item>
            <Item>不得以任何形式将账号转让、出借、出租或提供给他人使用。</Item>
          </Box>
          <P>
            2.4 如您丢失账号或密码，可通过申诉流程找回。您应妥善保管账号、密码及验证码，使用完毕后应安全退出。因账号密码泄露造成的损失由您自行承担。如发现任何非法使用账号的情况，请立即联系我们。
          </P>
          <P>
            2.5 如您申请注销账号，经我们审核同意后即解除与我们的合同关系。账号注销后，我们仍有权按法律法规要求保留必要的用户数据及行为记录，并对注销前的违法或违约行为行使本协议所约定的权利。
          </P>
        </Section>

        <Section title="3. 使用规范">
          <P>
            3.1 依据本协议，我们授予您一项可撤销的、不可转让的、非独占的合法使用本平台的权利。本协议未明示授权的其他一切权利仍由 Xup 保留。您在使用本服务生成、发布或传播内容时，应当：（1）主动核查内容的真实性、准确性，避免传播虚假信息；（2）以显著方式标明该内容系由人工智能生成；（3）避免发布和传播任何违反本协议使用规范的内容。
          </P>
          <P>
            3.2 您在使用本服务时，应遵守法律法规的要求和本协议的约定，遵守自愿、平等、公平及诚实信用原则，不利用本服务侵犯他人合法权益或谋取不正当利益。
          </P>
          <P>3.3 您不得通过本服务输入、生成或传播以下违法不良信息：</P>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            <Item>反对宪法所确定的基本原则的；</Item>
            <Item>危害国家安全、泄露国家秘密、颠覆国家政权、破坏国家统一的；</Item>
            <Item>损害国家荣誉和利益的；</Item>
            <Item>歪曲、丑化、亵渎、否定英雄烈士事迹和精神，以侮辱、诽谤等方式侵害英雄烈士姓名、肖像、名誉、荣誉的；</Item>
            <Item>宣扬恐怖主义、极端主义或者煽动实施恐怖活动、极端主义活动的；</Item>
            <Item>煽动民族仇恨、民族歧视，破坏民族团结的；</Item>
            <Item>破坏国家宗教政策，宣扬邪教和封建迷信的；</Item>
            <Item>散布谣言，扰乱经济秩序和社会秩序的；</Item>
            <Item>散布淫秽、色情、赌博、暴力、凶杀、恐怖或者教唆犯罪的；</Item>
            <Item>侮辱或者诽谤他人，侵害他人名誉、隐私和其他合法权益的；</Item>
            <Item>法律、行政法规禁止的其他内容。</Item>
          </Box>
          <P>
            3.4 为履行法定义务，Xup 有权对用户行为及信息进行审查，包括但不限于对生成请求内容进行审查、建立风险过滤机制等。您不得通过以下方式对抗本平台的内容安全管理机制：
          </P>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            <Item>使用变体、乱码、谐音等方式规避检测，输入或生成违规内容；</Item>
            <Item>通过假扮身份、反向诱导、越狱攻击等方式进行恶意攻击或投毒；</Item>
            <Item>未经授权，擅自去除或篡改本服务的鉴权措施、安全标识或内容标记。</Item>
          </Box>
          <P>
            3.5 您不得对本平台及其支持系统进行干扰、破坏或攻击，不得从事非法侵入网络、窃取数据、恶意耗用资源等危害平台安全的活动。
          </P>
          <P>3.6 您不得利用本服务用于以下不当目的：</P>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            <Item>违反法律法规、监管政策或侵犯第三方合法权益；</Item>
            <Item>可能对人身健康、心理、社会或经济产生严重有害影响的危险目的；</Item>
            <Item>侵犯知识产权、商业秘密以及其他违反商业道德的行为；</Item>
            <Item>未经授权将服务全部或部分进行复制、转让、出售或提供分许可；</Item>
            <Item>其他法律法规禁止或可能损害 Xup 利益的使用方式。</Item>
          </Box>
        </Section>

        <Section title="4. 积分、充值与虚拟产品">
          <P>
            4.1 本平台采用积分制度，用户通过充值人民币获得对应积分，用于消耗 AI 生成服务（视频生成、图片生成等）。积分仅限在本平台内使用，不可提现、不可转让、不可兑换为现金或其他形式的财产。
          </P>
          <P bold>
            4.2 【虚拟产品不可退款】用户通过本平台购买的积分及其他虚拟产品，一经充值成功即视为消费完成，属于数字内容虚拟商品。根据《中华人民共和国消费者权益保护法》第二十五条之规定，数字化商品不适用无理由退货，充值成功后概不退款。请您在充值前仔细确认所购规格及数量。
          </P>
          <P bold>
            4.3 因生成任务失败（包括 AI 服务商接口异常、生成内容不符合预期等）导致的积分消耗，本平台将根据实际情况进行退积分处理，具体以平台实际退还为准。退积分不等同于退款，退还的积分仍为平台虚拟积分，不可提现。
          </P>
          <P>
            4.4 如您发现账户存在异常充值或未经授权的消费，应立即联系我们处理。因您个人账号管理疏忽导致的积分损失，本平台不承担退款责任。
          </P>
          <P>
            4.5 本平台保留在合理范围内调整积分价格、消耗规则及服务内容的权利，调整前将通过官方渠道提前公告，不影响已购积分的正常使用。
          </P>
        </Section>

        <Section title="5. AI 生成内容使用">
          <P>
            5.1 您对通过本平台发送的所有生成请求及获取的响应内容负责。您声明并保证所提交的提示词（Prompt）及参考图片不侵犯任何第三方的知识产权、肖像权、名誉权、隐私权等合法权益，不涉及任何国家秘密或其他敏感数据。
          </P>
          <P>
            5.2 在符合法律规定和本协议条款的条件下，您对通过本平台生成的内容享有使用权利，但受相应底层 AI 服务提供商使用条款的约束，请在商用前自行了解相关限制。
          </P>
          <P>
            5.3 本平台所有 AI 生成内容均由第三方 AI 模型输出，可能存在错误或不准确之处，仅供参考，不构成任何专业建议。您应科学理性使用 AI 生成内容，并对依据该内容所作出的任何决策自行承担后果。
          </P>
        </Section>

        <Section title="6. 知识产权">
          <P>
            6.1 Xup 对本平台（包括但不限于软件、技术、程序、代码、用户界面、网页、文字、图表、版面设计、商标等）享有法律法规允许范围内的全部权利，包括但不限于著作权、商标权、专利权和其他知识产权。
          </P>
          <P>
            6.2 未经我们许可，请勿擅自使用 Xup 相关的任何商标、服务标记、商号、域名、LOGO 或其他显著品牌特征，不得将上述标识以任何方式展示、使用或申请注册。
          </P>
        </Section>

        <Section title="7. 违约责任与责任限制">
          <P>
            7.1 针对您违反本协议或其他服务条款的行为，我们有权独立判断，无需通知，对您采取警示提醒、限期改正、限制账号功能、暂停使用、关闭账号、禁止重新注册、删除相关内容等处置措施。对涉嫌违法犯罪的行为，我们将保存有关记录并依法向主管部门报告。由此产生的法律责任及第三方索赔，由您自行承担；因此给我们造成损失的，您应一并赔偿。
          </P>
          <P>7.2 本服务仅以"现状"提供，我们不对以下事项作出保证：</P>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            <Item>本服务在操作上不会中断、没有错误或持续稳定；</Item>
            <Item>在现有技术水平下能够纠正本服务的所有缺陷；</Item>
            <Item>所有第三方 AI 服务提供商的 API 可用性和稳定性；</Item>
            <Item>AI 生成内容的合法性、真实性、完整性、准确性及实用性；</Item>
            <Item>本服务能满足用户的所有要求。</Item>
          </Box>
          <P>
            7.3 因网络连接故障、电力故障、不可抗力（包括战争、政府行为、自然灾害等）、黑客攻击、第三方 AI 服务提供商的服务变更或中断，或任何其他不能合理控制的原因造成的服务不可用、信息延误或丢失，我们不承担责任。
          </P>
          <P>
            7.4 我们可能不时推出新功能，新功能测试期内可能存在服务不稳定情况，除法律有明确规定外，我们不对测试期功能的稳定性作出保证。
          </P>
        </Section>

        <Section title="8. 投诉与反馈">
          <P>
            如果您认为本服务涉及侵害您知识产权或其他权利，或者您发现任何违法、虚假信息及违反本协议的使用行为，或者您对我们的服务有任何意见与建议，可通过以下方式联系我们：
          </P>
          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
            <Item>官网反馈入口：登录平台后点击"联系我们"按钮</Item>
            <Item>邮箱：lixibin023@gmail.com</Item>
          </Box>
          <P>我们将对您的意见高度重视并依法采取处理措施。</P>
        </Section>

        <Section title="9. 法律适用与管辖">
          <P>
            9.1 本协议的订立、执行和解释及争议的解决均适用中华人民共和国大陆地区法律。
          </P>
          <P>
            9.2 本协议履行或解释发生争议时，双方应努力友好协商解决。如协商不成，任何一方均有权向有管辖权的法院提起诉讼。
          </P>
        </Section>

        <Section title="10. 协议修改">
          <P>
            10.1 为向您提供更好的服务，或满足法律法规、政策调整及技术变化的需要，我们会适时对本协议进行修订，修订内容构成本协议的组成部分。更新后的协议将通过官方网站或服务页面进行公示，公示即代替原协议条款。
          </P>
          <P>
            10.2 如您不接受修改后的协议，请立即停止使用本服务。您继续使用本服务，将被视为您已接受修改后的协议。
          </P>
        </Section>

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          © 2026 Xup &nbsp;·&nbsp;
          <Link href="/terms" underline="hover" color="inherit">用户协议</Link>
          &nbsp;·&nbsp;
          <Link href="mailto:lixibin023@gmail.com" underline="hover" color="inherit">联系我们</Link>
        </Typography>
      </Container>
    </Box>
  );
};

export default TermsPage;
